/**
 * CustomCommandsConfig Component
 *
 * Inline configuration component for custom AI commands that run before extraction.
 * Allows users to define natural language commands to execute on the page.
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 */

import React, { useState } from "react";
import { HiSparkles, HiPlus, HiTrash, HiChevronDown, HiChevronUp } from "react-icons/hi2";
import ToggleSwitch from "./ToggleSwitch";

const CustomCommandsConfig = ({ config, onChange }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleMasterToggle = () => {
    onChange({ ...config, enabled: !config.enabled });
  };

  const handleOnFailureChange = (value) => {
    onChange({ ...config, onFailure: value });
  };

  const addCommand = () => {
    const newCommand = {
      id: crypto.randomUUID(),
      label: "",
      prompt: "",
      enabled: true,
    };
    onChange({
      ...config,
      commands: [...(config.commands || []), newCommand],
    });
  };

  const updateCommand = (id, field, value) => {
    const updatedCommands = (config.commands || []).map((cmd) =>
      cmd.id === id ? { ...cmd, [field]: value } : cmd
    );
    onChange({ ...config, commands: updatedCommands });
  };

  const toggleCommand = (id) => {
    const updatedCommands = (config.commands || []).map((cmd) =>
      cmd.id === id ? { ...cmd, enabled: !cmd.enabled } : cmd
    );
    onChange({ ...config, commands: updatedCommands });
  };

  const removeCommand = (id) => {
    const updatedCommands = (config.commands || []).filter((cmd) => cmd.id !== id);
    onChange({ ...config, commands: updatedCommands });
  };

  const handleRetryChange = (field, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onChange({
        ...config,
        retryOptions: {
          ...config.retryOptions,
          [field]: field === "delayMs" ? numValue * 1000 : numValue,
        },
      });
    }
  };

  const commands = config.commands || [];
  const retryOptions = config.retryOptions || { maxAttempts: 2, delayMs: 2000 };

  return (
    <div className="space-y-2.5">
      {/* Master Toggle & Header */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="flex items-center">
            <div className="p-1.5 rounded-lg bg-cyan-100 dark:bg-cyan-500/15 mr-2">
              <HiSparkles className="text-cyan-500 dark:text-cyan-400 w-3 h-3" />
            </div>
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Custom Commands</h3>
          </div>
          <ToggleSwitch checked={config.enabled} onChange={handleMasterToggle} />
        </div>

        {config.enabled && (
          <div className="space-y-3">
            {/* On Failure Behavior */}
            <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">If command fails</label>
                <p className="text-2xs text-gray-400 dark:text-gray-500">What happens after retries are exhausted</p>
              </div>
              <div className="relative">
                <select
                  value={config.onFailure || "continue"}
                  onChange={(e) => handleOnFailureChange(e.target.value)}
                  className="appearance-none bg-white dark:bg-gray-800/60 border border-gray-300 dark:border-gray-600/40 rounded-lg pl-2 pr-7 py-1 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-500/50 cursor-pointer"
                >
                  <option value="continue">Ignore and continue</option>
                  <option value="abort">Fail with error</option>
                </select>
                <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Commands List */}
            {commands.length > 0 && (
              <div className="space-y-2">
                {commands.map((command, index) => (
                  <div
                    key={command.id}
                    className={`p-3 rounded-xl border transition-colors ${
                      command.enabled
                        ? "bg-cyan-50 dark:bg-gradient-to-br dark:from-cyan-500/10 dark:to-blue-500/5 border-cyan-200 dark:border-cyan-500/20"
                        : "bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700/30 opacity-60"
                    }`}
                  >
                    {/* Command Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ToggleSwitch
                          checked={command.enabled}
                          onChange={() => toggleCommand(command.id)}
                        />
                        <span className="text-xs text-gray-400">#{index + 1}</span>
                      </div>
                      <button
                        onClick={() => removeCommand(command.id)}
                        className="p-1 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                        title="Remove command"
                      >
                        <HiTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Label Input */}
                    <input
                      type="text"
                      placeholder="Command name (e.g., Accept age verification)"
                      value={command.label}
                      onChange={(e) => updateCommand(command.id, "label", e.target.value)}
                      className="w-full bg-white dark:bg-gray-900/40 border border-gray-300 dark:border-gray-700/40 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-500/50 mb-2"
                    />

                    {/* Prompt Textarea */}
                    <textarea
                      placeholder="Natural language command (e.g., If there is an age verification popup, click the button to confirm I am over 18)"
                      value={command.prompt}
                      onChange={(e) => updateCommand(command.id, "prompt", e.target.value)}
                      rows={2}
                      className="w-full bg-white dark:bg-gray-900/40 border border-gray-300 dark:border-gray-700/40 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-500/50 resize-none"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Add Command Button */}
            <button
              onClick={addCommand}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl border border-dashed border-gray-300 dark:border-gray-600/40 text-gray-500 dark:text-gray-400 hover:border-cyan-400 dark:hover:border-cyan-500/40 hover:text-cyan-500 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-500/5 transition-colors"
            >
              <HiPlus className="w-4 h-4" />
              <span className="text-sm">Add Command</span>
            </button>

            {/* Advanced Settings Toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
            >
              {showAdvanced ? (
                <HiChevronUp className="w-3.5 h-3.5" />
              ) : (
                <HiChevronDown className="w-3.5 h-3.5" />
              )}
              <span>Advanced settings</span>
            </button>

            {/* Advanced Settings */}
            {showAdvanced && (
              <div className="space-y-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/30">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Retry attempts</label>
                    <p className="text-2xs text-gray-400 dark:text-gray-500">Per command</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={retryOptions.maxAttempts}
                    onChange={(e) => handleRetryChange("maxAttempts", e.target.value)}
                    className="w-16 bg-white dark:bg-gray-900/40 border border-gray-300 dark:border-gray-700/40 rounded-lg px-2 py-1 text-sm text-gray-700 dark:text-gray-200 text-center focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-500/50"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Retry delay</label>
                    <p className="text-2xs text-gray-400 dark:text-gray-500">Seconds between retries</p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={Math.round(retryOptions.delayMs / 1000)}
                    onChange={(e) => handleRetryChange("delayMs", e.target.value)}
                    className="w-16 bg-white dark:bg-gray-900/40 border border-gray-300 dark:border-gray-700/40 rounded-lg px-2 py-1 text-sm text-gray-700 dark:text-gray-200 text-center focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-500/50"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {!config.enabled && (
          <p className="text-xs text-gray-400 dark:text-gray-500 px-2">
            Enable to add AI-powered commands that run before extraction
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomCommandsConfig;
